/*
 * Multicolumn — Swiper-backed slider for the `_multicolumn` block.
 *
 * The block renders two trees inside [data-multicolumn]: a CSS Grid
 * tree (.multicolumn__grid) and a slider tree (.multicolumn__slider).
 * CSS toggles which tree is visible at each breakpoint based on the
 * data-layout-{d,t,m} attributes. This module wires up Swiper on the
 * slider tree, but only when at least one breakpoint runs in slider
 * mode — grid-only configs skip init entirely.
 *
 * Per-instance pattern (one carousel per [data-multicolumn]) so
 * multiple Multicolumn blocks on the same page each get their own
 * Swiper state. Re-runs on shopify:section:load when the customizer
 * reloads a section.
 *
 * Slider tree DOM contract:
 *   .multicolumn__slider
 *     button.multicolumn__nav-button--prev   (optional)
 *     .swiper
 *       .swiper-wrapper
 *         <direct children>  — wrapped in .swiper-slide here, at init
 *       .multicolumn__pagination             (optional)
 *     button.multicolumn__nav-button--next   (optional)
 *
 * Swiper is loaded as a UMD bundle by a <script> tag in
 * snippets/scripts.liquid earlier in the document, exposing a global
 * `Swiper` constructor — same pattern testimonials-carousel uses.
 */

const Swiper = window.Swiper;

const SELECTOR = '[data-multicolumn]';
const INSTANCE_KEY = '_multicolumnInstance';

class Multicolumn {
  constructor(root) {
    this.root = root;
    this.swiper = null;

    // If grid-only at every breakpoint, no Swiper needed.
    const layouts = [
      root.dataset.layoutD,
      root.dataset.layoutT,
      root.dataset.layoutM,
    ];
    if (!layouts.includes('slider')) return;

    this.init();
  }

  init() {
    const sliderRoot = this.root.querySelector('.multicolumn__slider');
    if (!sliderRoot) return;
    const swiperEl = sliderRoot.querySelector('.swiper');
    const wrapper = swiperEl?.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    // Filter the slider tree to the chosen card type, then mark the
    // matching cards as .swiper-slide.
    //
    // The Multicolumn renders ALL children (every card type) into
    // both the grid and slider trees, then CSS hides mismatches via
    // display: none. That's enough for the grid (display:none removes
    // the item from the grid flow), but Swiper computes slide layout
    // from the raw .swiper-wrapper children before the CSS hide takes
    // effect, leaving a blank slot for each mismatched card. Removing
    // mismatches from the slider tree here drops them from Swiper's
    // count entirely. They stay in the grid tree + the block data
    // tree, so switching Card Type back still restores them.
    const cardTypeMap = {
      'multicolumn--card-type-content':    'card',
      'multicolumn--card-type-flip':       'flip-card',
      'multicolumn--card-type-collection': 'collection-card',
    };
    let matchClass = '';
    for (const [wrapperClass, cardClass] of Object.entries(cardTypeMap)) {
      if (this.root.classList.contains(wrapperClass)) {
        matchClass = cardClass;
        break;
      }
    }

    Array.from(wrapper.children).forEach((child) => {
      if (matchClass && !child.classList.contains(matchClass)) {
        child.remove();
        return;
      }
      child.classList.add('swiper-slide');
    });

    const ds = this.root.dataset;
    const spvD = parseFloat(ds.spvD) || 3;
    const spvT = parseFloat(ds.spvT) || 2;
    const spvM = parseFloat(ds.spvM) || 1.2;
    const gapD = parseFloat(ds.gapD) || 16;
    const gapT = parseFloat(ds.gapT) || 16;
    const gapM = parseFloat(ds.gapM) || 16;
    const loop = ds.loop === 'true';
    const autoplay = ds.autoplay === 'true';
    const autoplayDelay = (parseFloat(ds.autoplayDelay) || 5) * 1000;
    const showNavigation = ds.showNavigation === 'true';
    const showPagination = ds.showPagination === 'true';

    /** @type {any} */
    const options = {
      // Mobile-first defaults; breakpoints below override at >=768
      // (tablet) and >=992 (desktop). Matches Bootstrap-style breakpoint
      // boundaries used by the rest of the theme.
      slidesPerView: spvM,
      spaceBetween: gapM,
      loop,
      autoplay: autoplay
        ? { delay: autoplayDelay, disableOnInteraction: false, pauseOnMouseEnter: true }
        : false,
      breakpoints: {
        768: { slidesPerView: spvT, spaceBetween: gapT },
        992: { slidesPerView: spvD, spaceBetween: gapD },
      },
      // watchOverflow (default true in modern Swiper) puts the slider
      // into "locked" mode when there aren't enough slides to scroll.
      // We listen for `lock` / `unlock` events below to toggle a
      // data attribute that CSS uses to hide arrows + pagination.
      watchOverflow: true,
      observer: true,
      observeParents: true,
    };

    if (showNavigation) {
      // Arrows now come from the shared slideshow-arrow snippet,
      // which writes .slideshow-control--{action} on the button.
      const prevEl = sliderRoot.querySelector('.slideshow-control--previous');
      const nextEl = sliderRoot.querySelector('.slideshow-control--next');
      if (prevEl && nextEl) {
        options.navigation = { prevEl, nextEl };
      }
    }

    if (showPagination) {
      const paginationEl = sliderRoot.querySelector('.multicolumn__pagination');
      if (paginationEl) {
        options.pagination = { el: paginationEl, clickable: true };
      }
    }

    this.swiper = new Swiper(swiperEl, options);

    // Sync the locked-state flag from Swiper. When fewer slides than
    // slidesPerView, Swiper enters "locked" mode and stops responding
    // to swipe / autoplay; we hide arrows + pagination via CSS so the
    // UI doesn't advertise navigation it can't perform.
    const setLocked = (locked) => {
      this.root.dataset.locked = locked ? 'true' : 'false';
    };
    setLocked(this.swiper.isLocked);
    this.swiper.on('lock', () => setLocked(true));
    this.swiper.on('unlock', () => setLocked(false));

    this.root[INSTANCE_KEY] = this;
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    delete this.root[INSTANCE_KEY];
  }
}

function initAll(scope) {
  scope.querySelectorAll(SELECTOR).forEach((root) => {
    // Tear down any prior instance so a customizer re-render doesn't
    // leave a zombie Swiper attached to recycled DOM.
    if (root[INSTANCE_KEY]) {
      root[INSTANCE_KEY].destroy();
    }
    new Multicolumn(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
} else {
  initAll(document);
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initAll(event.target);
  }
});
